import {Injectable} from '@angular/core';
import {PaymentMethod, PaymentProvider, PaymentType} from '@features/payment/domain/model/payment.model';
import {
  CheckoutEntryPayload,
  CheckoutPayload,
  PaymentPayload,
} from '@features/payment/infrastructure/dto/payment-payload.dto';
import {Payment} from '@features/payment/domain/entities/payment.entity';
import {OrderItemDTO} from '@features/sales/domain/models/order/order-item.model';
import {ProductType} from '@features/sales/domain/models/product/product.model';

@Injectable({providedIn: 'root'})
export class CheckoutPayloadMapper {

  toCheckoutPayload(payments: Payment[]): CheckoutPayload {
    this.ensureValidPayments(payments);

    const {order} = payments[0];

    if (!order) {
      throw new Error('[CheckoutPayloadMapper] Payment must have an order');
    }

    return {
      accountId: order.accountId,
      locationId: order.locationId,
      serviceId: order.serviceId,
      waiterId: order.waiterId,
      cashDrawerId: order.cashDrawerId,
      entries: this.mapEntries(payments),
      payments: this.mapPayments(payments),
      ...this.mapWalletInfo(payments),
    };
  }

  private mapEntries(payments: Payment[]): CheckoutEntryPayload[] {
    return payments
      .flatMap(p => p.order?.entries ?? [])
      .map(entry => this.toEntryPayload(entry));
  }

  private toEntryPayload(entry: OrderItemDTO): CheckoutEntryPayload {
    return {
      item: {
        ...entry.item,
        type: entry.item.type === ProductType.COMPLEMENT ? ProductType.PRODUCT : entry.item.type,
      },
      quantity: entry.quantity,
      amount: entry.amount,
      customUnitPrice: entry.customUnitPrice ?? null,
      discount: entry.discount ?? null,
      additionalProduct: entry.additionalProduct ?? null,
    };
  }

  private mapPayments(payments: Payment[]): PaymentPayload[] {
    return payments.map(p => ({
      amount: p.amount,
      paymentMethod: p.method,
      paymentType: p.paymentType ?? PaymentType.FULL,
      provider: p.provider ?? PaymentProvider.DEFAULT,
      gateway: p.gateway,
      ...(p.sourceId && {sourceId: p.sourceId}),
      ...(p.supplement && {supplement: p.supplement}),
    }));
  }

  private mapWalletInfo(payments: Payment[]): { walletId?: string } {
    const walletPayment = payments.find(p => p.method === PaymentMethod.WALLET);

    return walletPayment?.walletId ? {walletId: walletPayment.walletId} : {};
  }

  private ensureValidPayments(payments: Payment[]): void {
    if (!payments.length) {
      throw new Error('[CheckoutPayloadMapper] At least one payment is required');
    }
  }
}
